// Input layer (spec §8.1–8.3): ONE input, no format tabs. Format is auto-detected
// per pane on paste/upload and surfaced as a quiet, click-to-override pill; if the
// two panes detect different formats we say so (red pill) instead of guessing.
// Owns getting data *in* and the detection state; the results surface consumes
// getPayload(). Comparison options live in the Options popover (main.js wires the
// popover open/close and re-runs).
import { load as loadYaml } from 'js-yaml';
import { DEMOS } from '../data/demos.js';
import { detectFormat, formatToTab } from '../lib/detect.js';
import { csvColumns } from '../lib/csv.js';
import { unsupportedFormatForPaste } from '../lib/unsupported-format.js';
import { checkInputBytes, textBytes } from '../lib/input-limits.js';
import { createDocumentLoader, documentLabel } from '../lib/document-load.js';
import { readBinary } from '../lib/worker-client.js';
import { mountFileViewer } from './file-viewer.js';
import { showNotice } from './notice-dialog.js';
import { isArrayOfObjects, topLevelColumns, pickKeyColumn } from '../engine/records/keys.js';

const $ = (sel) => document.querySelector(sel);
const LARGE_INPUT = 2_000_000; // chars — inform, never block (spec §1)
// The pane names as tool.js prints them, for notices that must say which side.
const PANE_LABEL = { left: 'Original', right: 'Changed' };

const DISPLAY = { json: 'JSON', yaml: 'YAML', csv: 'CSV', tsv: 'TSV', text: 'Text', parquet: 'Parquet' };

// Best-effort parse for the key selector (JSON first, YAML fallback). Null on fail.
function tryParseStructured(text) {
  const t = (text || '').trim();
  if (!t) return null;
  try {
    return JSON.parse(t);
  } catch {
    try {
      return loadYaml(t);
    } catch {
      return null;
    }
  }
}

export function initInput({ initial = 'text', debounceMs = 250, onChange, onUnsupported, onDataset = () => {} } = {}) {
  const els = {
    leftTa: $('#leftTa'),
    rightTa: $('#rightTa'),
    leftFile: $('#leftFile'),
    rightFile: $('#rightFile'),
    keycolWrap: $('#keycolWrap'),
    keycolSelect: $('#keycolWrap select'),
    detected: $('#detected'),
    detText: $('#detText'),
    sizeNote: $('#sizeNote'),
  };

  // format = the V1 engine tab (text|json|csv); overridden = the user picked it;
  // mismatch = the two panes disagree on format.
  const state = { format: initial, overridden: false, mismatch: false, keyable: false };
  const documents = { left: null, right: null };
  const sourceBytes = { left: null, right: null };
  const sourceText = { left: null, right: null };
  const inputBytes = which => documents[which] || sourceText[which] === $(`#${which}Ta`).value
    ? sourceBytes[which] ?? 0 : textBytes($(`#${which}Ta`).value);
  const unmountViewer = { left: () => {}, right: () => {} };
  // Upload/drop orchestration is shared with the standalone Parquet viewer page
  // (src/lib/document-load.js) — one set of allowlist, budget and stale-read
  // rules for both surfaces (G51).
  const loaders = {};
  for (const which of ['left', 'right']) {
    loaders[which] = createDocumentLoader({
      readBinary,
      otherBytes: () => inputBytes(which === 'left' ? 'right' : 'left'),
      onStatus: (status) => {
        if (status.state === 'idle') clearUploadError(which);
        else if (status.state === 'busy') showUploadProgress(which, status.message);
        else refuseUpload(which, status.message, status.reason === 'oversize');
      },
      onUnsupported: (kind) => onUnsupported?.(kind),
    });
  }
  const notify = (detected) => onChange?.({ ...state, detected });

  // Load the code move-detection demo on demand (the "Try with sample" button).
  // Content only — no fake filenames; those belong to real uploads.
  function loadSample() {
    clearFile('left', false);
    clearFile('right', false);
    clearUploadError('left');
    clearUploadError('right');
    const d = DEMOS.text;
    els.leftTa.value = d.left;
    els.rightTa.value = d.right;
    els.leftFile.textContent = '';
    els.rightFile.textContent = '';
    state.overridden = false;
    runDetect();
  }

  // The "matched by" key selector appears for record formats: CSV/TSV, and
  // JSON/YAML that parse to an array of objects (so arrays match by id like CSV).
  function refreshKeycol() {
    const tab = formatToTab(state.format);
    const sample = els.leftTa.value.trim() ? els.leftTa.value : els.rightTa.value;
    let cols = null;
    let jsonKey = false;
    if (documents.left || documents.right) {
      cols = documents.left && documents.right
        ? documents.left.columns.filter(c => documents.right.columns.includes(c))
        : (documents.left || documents.right).columns;
      jsonKey = true;
    } else if (tab === 'csv') {
      cols = csvColumns(sample, state.format);
    } else if (tab === 'json') {
      const v = tryParseStructured(sample);
      if (isArrayOfObjects(v)) {
        cols = topLevelColumns(v);
        jsonKey = true;
      }
    }
    state.keyable = !!(cols && cols.length);
    els.keycolWrap.style.display = state.keyable ? 'inline-flex' : 'none';
    if (state.keyable && els.keycolSelect) {
      const prev = els.keycolSelect.value;
      const positional = prev === '' && !!els.keycolSelect.querySelector('option[value=""]');
      const dataset = documents.left?.dataset || documents.right?.dataset;
      els.keycolSelect.innerHTML = (documents.left || documents.right ? `<option value="">${dataset ? 'Choose a key' : 'Row position'}</option>` : '') + cols.map((c) => `<option value="${escapeHtml(c)}">${escapeHtml(keyLabel(c, cols))}</option>`).join('');
      // JSON arrays default to the auto-detected id-ish key; CSV to the first column.
      els.keycolSelect.value = positional && !dataset && (documents.left || documents.right) ? '' : cols.includes(prev) ? prev : jsonKey ? pickKeyColumn(cols) || (documents.left || documents.right ? '' : cols[0]) : cols[0];
    }
  }

  // Paint the detection pill. `rich` is the fine-grained format (json/yaml/csv/…),
  // so the pill can say "YAML" even though the engine tab is "json".
  function setPill({ empty, mismatch, rich } = {}) {
    if (!els.detText) return;
    els.detected.classList.toggle('mismatch', !!mismatch);
    if (empty) {
      els.detText.textContent = 'paste to detect';
      els.detected.dataset.manual = 'false';
      return;
    }
    if (mismatch) {
      const [a, b] = mismatch;
      els.detText.innerHTML = `<b>${DISPLAY[a]}</b> vs <b>${DISPLAY[b]}</b> — cross-format`;
      els.detected.dataset.manual = 'false';
      return;
    }
    if (state.overridden) {
      els.detText.innerHTML = `format: <b>${DISPLAY[rich] || DISPLAY[state.format]}</b> · manual`;
      els.detected.dataset.manual = 'true';
    } else {
      els.detText.innerHTML = `detected <b>${DISPLAY[rich] || DISPLAY[state.format]}</b>`;
      els.detected.dataset.manual = 'false';
    }
  }

  function checkSize() {
    if (!els.sizeNote) return;
    const total = els.leftTa.value.length + els.rightTa.value.length;
    if (total > LARGE_INPUT) {
      els.sizeNote.textContent = `Large input (~${(total / 1_000_000).toFixed(1)} MB) — comparison runs in a background worker, so this tab stays responsive.`;
      els.sizeNote.hidden = false;
    } else {
      els.sizeNote.hidden = true;
    }
  }

  // Re-detect from the current content of both panes. Auto-switches the format
  // unless the user overrode it; flags a cross-format mismatch instead of guessing.
  function runDetect() {
    if (documents.left || documents.right) {
      state.format = 'parquet';
      state.overridden = false;
      state.mismatch = !!((documents.left && !documents.right && els.rightTa.value.trim()) || (documents.right && !documents.left && els.leftTa.value.trim()));
      setPill({ rich: 'parquet', mismatch: state.mismatch ? ['parquet', detectFormat(documents.left ? els.rightTa.value : els.leftTa.value)] : null });
      refreshKeycol();
      checkSize();
      notify('parquet');
      return 'parquet';
    }
    const dl = els.leftTa.value.trim() ? detectFormat(els.leftTa.value) : null;
    const dr = els.rightTa.value.trim() ? detectFormat(els.rightTa.value) : null;

    if (!dl && !dr) {
      state.mismatch = false;
      setPill({ empty: true });
      refreshKeycol();
      checkSize();
      notify(state.format);
      return state.format;
    }

    // Mismatch only when both sides are recognized (non-text) AND land on
    // different engine tabs — json-vs-yaml or csv-vs-tsv are NOT a mismatch.
    const bothStructured = dl && dr && dl !== 'text' && dr !== 'text';
    if (bothStructured && formatToTab(dl) !== formatToTab(dr) && !state.overridden) {
      state.mismatch = true;
      state.format = 'text'; // never silently guess — fall back to a safe line diff
      setPill({ mismatch: [dl, dr] });
    } else {
      state.mismatch = false;
      const rich = dl || dr;
      if (!state.overridden) state.format = formatToTab(rich);
      setPill({ rich });
    }
    refreshKeycol();
    checkSize();
    notify(dl || dr || state.format);
    return dl || dr;
  }

  // Manual override (from the pill menu, a shared link, or ?format=). `fmt` may be
  // fine-grained (yaml/tsv) or an engine tab (json/csv/text).
  function setFormat(fmt, { manual = false } = {}) {
    if (documents.left || documents.right) { runDetect(); return; }
    // Parquet is a decoded document, never a reading of pasted text. With no file
    // open there is nothing to switch to, and formatToTab('parquet') collapses to
    // 'text' — so accepting it would leave the pill claiming "Parquet · manual"
    // over a text diff. Ignore it here, at the state machine, not just in the menu.
    if (fmt === 'parquet') { runDetect(); return; }
    // "Auto" clears the override and re-detects from the current content.
    if (fmt === 'auto') {
      state.overridden = false;
      runDetect();
      return;
    }
    const rich = fmt;
    state.format = formatToTab(fmt);
    state.mismatch = false;
    if (manual) state.overridden = true;
    setPill({ rich });
    refreshKeycol();
    checkSize();
    notify(state.format);
  }

  function clearUploadError(which) {
    const error = $(`#${which}UploadError`);
    error.textContent = '';
    error.hidden = true;
  }

  // Progress ("Reading file…", "Validating 3 of 20…") stays inline under its pane:
  // it resolves by itself and needs no answer.
  function showUploadProgress(which, message) {
    const status = $(`#${which}UploadError`);
    status.textContent = message;
    status.hidden = false;
  }

  // A refused selection or an input over the budget is a notice the reader must
  // acknowledge: the pane did not change, and a line of small text under it was
  // easy to miss. The message already says the previous input was kept.
  function refuseUpload(which, message, oversize = false) {
    clearUploadError(which);
    showNotice({ title: oversize ? 'Too large to compare' : `Couldn’t load into ${PANE_LABEL[which]}`, message });
  }

  function clearFile(which, detect = true) {
    loaders[which].invalidate();
    documents[which] = null;
    sourceBytes[which] = null;
    sourceText[which] = null;
    if (!documents.left && !documents.right && state.format === 'parquet') { state.format = 'text'; state.overridden = false; }
    unmountViewer[which]();
    $(`#${which}FileViewer`).hidden = true;
    $(`#${which}Ta`).hidden = false;
    $(`#${which}Ta`).value = '';
    $(`#${which}File`).textContent = '';
    $(`[data-clear="${which}"]`).hidden = true;
    clearUploadError(which);
    const comparisonError = $('#comparisonError');
    if (comparisonError) comparisonError.hidden = true;
    if (detect) runDetect();
  }

  async function loadFile(file, which) {
    if (!file) return;
    return loadFiles([file], which);
  }

  async function loadFiles(files, which, options = {}) {
    applyLoaded(which, await loaders[which].load(files, options));
  }

  // The pane-specific half of a load: swap textarea/viewer, label the source and
  // re-detect. A null result means the loader rejected or superseded the read —
  // the previous complete document stays exactly as it was.
  function applyLoaded(which, result) {
    if (!result) return;
    const { document, bytes, entries, directory } = result;
    // Shape only (single file / several parts / a folder) so Parquet adoption in
    // the comparison tool is visible. No name, path, part count or row count.
    if (document.kind === 'binary') onDataset(directory ? 'folder' : entries.length > 1 ? 'multi' : 'single');
    sourceBytes[which] = bytes;
    unmountViewer[which]();
    documents[which] = document.kind === 'binary' ? document : null;
    const viewer = $(`#${which}FileViewer`);
    viewer.hidden = !documents[which];
    $(`#${which}Ta`).hidden = !!documents[which];
    $(`[data-clear="${which}"]`).hidden = false;
    if (documents[which]) unmountViewer[which] = mountFileViewer(viewer, document);
    const text = document.kind === 'text' ? document.text : '';
    sourceText[which] = document.kind === 'text' ? text : null;
    const label = documentLabel({ document, entries, directory });
    if (which === 'left') {
      els.leftTa.value = text;
      els.leftFile.textContent = label;
    } else {
      els.rightTa.value = text;
      els.rightFile.textContent = label;
    }
    state.overridden = false; // a fresh file should re-detect
    runDetect();
  }

  // ---- wiring ----
  let timer;
  const onInput = () => {
    clearTimeout(timer);
    timer = setTimeout(runDetect, debounceMs);
  };
  els.leftTa.addEventListener('input', onInput);
  els.rightTa.addEventListener('input', onInput);
  const onPaste = (event) => {
    const which = event.target === els.leftTa ? 'left' : 'right';
    if (event.clipboardData?.files?.length) {
      event.preventDefault();
      loadFiles(event.clipboardData.files, which);
      return;
    }
    const ta = event.target;
    const pasted = event.clipboardData?.getData('text/plain') || '';
    try {
      checkInputBytes(textBytes(ta.value.slice(0, ta.selectionStart) + pasted + ta.value.slice(ta.selectionEnd)), inputBytes(which === 'left' ? 'right' : 'left'));
    } catch (error) {
      event.preventDefault();
      refuseUpload(which, error.message, true);
      return;
    }
    const unsupported = unsupportedFormatForPaste(event.clipboardData);
    if (unsupported) onUnsupported?.(unsupported);
  };
  els.leftTa.addEventListener('paste', onPaste);
  els.rightTa.addEventListener('paste', onPaste);
  // A manual edit means the pane no longer holds the uploaded file — clear the
  // stale filename label (programmatic value sets don't fire 'input', so uploads
  // and demos keep theirs).
  for (const which of ['left', 'right']) {
    $(`#${which}Ta`).addEventListener('input', () => {
      loaders[which].invalidate();
      sourceBytes[which] = null;
      $(`#${which}File`).textContent = '';
      clearUploadError(which);
    });
    $(`[data-clear="${which}"]`).addEventListener('click', () => clearFile(which));
  }

  document.querySelectorAll('.upload-btn').forEach((btn) => {
    const input = document.getElementById(btn.dataset.input);
    const which = btn.closest('.pane').dataset.pane;
    btn.addEventListener('click', () => input.click());
    input?.addEventListener('change', (e) => {
      if (e.target.files.length) loadFiles(e.target.files, which, { directory: e.target.hasAttribute('webkitdirectory') });
      e.target.value = ''; // allow re-uploading the same file
    });
  });

  document.querySelectorAll('.pane').forEach((pane) => {
    const which = pane.dataset.pane;
    pane.addEventListener('dragover', (e) => {
      e.preventDefault();
      pane.classList.add('dragover');
    });
    pane.addEventListener('dragleave', (e) => {
      if (!pane.contains(e.relatedTarget)) pane.classList.remove('dragover');
    });
    pane.addEventListener('drop', async (e) => {
      e.preventDefault();
      pane.classList.remove('dragover');
      applyLoaded(which, await loaders[which].loadDrop(e.dataTransfer));
    });
  });

  // Boxes start empty — the tool asks for nothing (spec §8.3). The user loads the
  // demo via "Try with sample"; detection runs on whatever is present ("paste to
  // detect" while empty).
  state.format = initial;
  runDetect();

  function validateSize() {
    for (const which of ['left', 'right']) {
      try { checkInputBytes(inputBytes(which), inputBytes(which === 'left' ? 'right' : 'left')); }
      catch (error) { refuseUpload(which, error.message, true); return false; }
    }
    return true;
  }

  function getPayload() {
    return {
      left: els.leftTa.value,
      right: els.rightTa.value,
      format: state.format,
      ...(documents.left || documents.right ? { leftDocument: documents.left, rightDocument: documents.right } : {}),
      // key applies to any record format (CSV + JSON/YAML arrays) when the
      // selector is showing; the engines auto-detect a key when this is null.
      keyColumn: state.keyable && els.keycolSelect ? els.keycolSelect.value || null : null,
      options: {
        ignoreWhitespace: !!$('#optWhitespace')?.checked,
        ignoreCase: !!$('#optCase')?.checked,
        ignoreKeyOrder: !!$('#optKeyOrder')?.checked,
      },
    };
  }

  return { state, validateSize, runDetect, setFormat, getPayload, loadSample, loadFile, loadFiles, clearFile, isLoading: () => loaders.left.isLoading() || loaders.right.isLoading() };
}

// The option's VALUE is the exact column name, because it is passed to the engine
// as keyColumn. It must be an explicit attribute: an <option> without one takes
// its value from its text with whitespace stripped and collapsed, so ` id `
// silently became `id` — a name the engine does not have (REL-088). The LABEL is
// for reading: whitespace runs (including a quoted header's newline) shown as one
// space and the ends trimmed — unless that would make two options look alike, in
// which case the exact name is shown in quotes so they stay distinguishable.
const tidy = (name) => name.replace(/\s+/g, ' ').trim();
function keyLabel(name, all) {
  const label = tidy(name);
  if (label === name) return name;
  const clash = !label || all.some((other) => other !== name && tidy(other) === label);
  return clash ? JSON.stringify(name) : label;
}

function escapeHtml(s) {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}
